import { WorkspaceClosedError } from '../router/spaces-resources'
import type { Tel } from '@w/tel'
import { RpcError } from '@w/rpc'
import type { WorkspaceDb } from '../db/workspace-tables'
import { updatePageTranslations, type ProcessedPage, type TranslateTexts } from './page-translations'
import type { ItemReadResource } from '../dead-letter-queue/entries'

/** Independent lifetime; one failed call disables this session's maintenance. */
export function createPageTranslationsBackground(input: {
  db: WorkspaceDb
  readResource?: ItemReadResource
  translate: TranslateTexts
  language: () => string
  skipPageIds: () => string[]
  tel: Tel
  onWarmed?: () => void
}) {
  const editing = new Set<string>()
  const requested = new Set<string>()
  const pending = new Set<string>()
  const processedByLanguage = new Map<string, Map<string, ProcessedPage>>()
  const lifetime = new AbortController()
  const cancelled = Promise.withResolvers<void>()
  let disabled = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let running = Promise.resolve()
  function flush() {
    if (timer === undefined) return
    clearTimeout(timer)
    timer = undefined
    running = running.then(run)
  }
  async function run() {
    if (lifetime.signal.aborted || disabled || requested.size === 0) return
    const work = input.tel.span({ name: 'page.translate', target: 'job', input: {} }, async tel => {
      const language = input.language()
      let processedPages = processedByLanguage.get(language)
      if (!processedPages) { processedPages = new Map(); processedByLanguage.set(language, processedPages) }
      const skipped = new Set([...input.skipPageIds(), ...editing])
      const pageIds = [...requested].filter(pageId => !skipped.has(pageId))
      const revalidatePageIds = pageIds.filter(pageId => pending.delete(pageId))
      // Requested pages remain eligible for repair after later commits; unopened pages never enter this set.
      try {
        const report = await updatePageTranslations({ db: input.db, readResource: input.readResource, language, processedPages, pageIds, revalidatePageIds, skipPageIds: [...skipped],
          translate: request => input.translate({ ...request, signal: lifetime.signal }, tel),
          isClosed: () => lifetime.signal.aborted || disabled,
        }, tel)
        if (!lifetime.signal.aborted && report.sent > 0) input.onWarmed?.()
      } catch (error) {
        if (lifetime.signal.aborted || error instanceof WorkspaceClosedError) return
        if (!(error instanceof RpcError) || error.status !== 403) throw error
        // Provider authorization refusal is expected; recover before the job
        // boundary reports an error (translation-failure-disables-session).
        disabled = true
        tel.currentSpan.end({ error }, { errorLevel: 'warning' })
        if (!lifetime.signal.aborted) tel.warn('Page translation unavailable; maintenance disabled for this session.', { status: error.status })
      }
    }).catch(error => {
      disabled = true
      if (!lifetime.signal.aborted) input.tel.warn('Page translation unavailable; maintenance disabled for this session.', { error })
    })
    await Promise.race([cancelled.promise, work])
  }
  function update() {
    if (lifetime.signal.aborted || disabled) return
    clearTimeout(timer)
    timer = setTimeout(flush, 1_000)
  }
  function request(pageId: string) {
    if (lifetime.signal.aborted || disabled) return
    requested.add(pageId)
    pending.add(pageId)
    update(); flush()
  }
  return { update, request,
    registerEditor(pageId: string) {
      editing.add(pageId)
      return () => {
        editing.delete(pageId)
        update()
      }
    },
    async open(pageId: string) {
      if (lifetime.signal.aborted || disabled) return
      const rows = await input.db.query`SELECT ${input.db.pageTranslations.pageId} FROM ${input.db.pageTranslations} WHERE ${input.db.pageTranslations.pageId.eq(pageId)} AND ${input.db.pageTranslations.language.eq(input.language())} LIMIT 1`.execute({ pageId: input.db.pageTranslations.pageId })
      request(pageId)
      if (rows.length === 0) await running
    },
    waitForIdle() { flush(); return running },
    close() { clearTimeout(timer); timer = undefined; lifetime.abort(); cancelled.resolve() },
  }
}
